// Import orchestrator: file -> parse -> past-customer detection -> upsert.

import { SOURCE } from '../constants';
import { sniffPlatform, parseCsvText } from './parseCsv';
import { parseEtsyOrders } from './etsy';
import { parseEbayOrders } from './ebay';
import { parseShopifyOrders } from './shopify';
import { buildDocId } from '../lib/order';
import { upsertOrder, detectRepeatCustomer } from '../services/ordersService';
import { getDeliveryRules } from '../services/configService';
import { rebuildStats } from '../../kpis/statsService';

const PARSERS = {
  [SOURCE.ETSY]: parseEtsyOrders,
  [SOURCE.EBAY]: parseEbayOrders,
  [SOURCE.SHOPIFY]: parseShopifyOrders,
};

/**
 * Parse a File into { source, orders } without writing anything (preview).
 * `rules` are the (editable) delivery rules; loaded if not supplied.
 */
export async function parseFile(file, rules) {
  const deliveryRules = rules || (await getDeliveryRules());
  const text = await file.text();
  const rows = parseCsvText(text);
  const source = sniffPlatform(file.name, rows);
  if (!source || !PARSERS[source]) {
    return { source: null, orders: [], error: `Could not detect platform for "${file.name}"` };
  }
  const orders = PARSERS[source](text, deliveryRules);
  return { source, orders };
}

/**
 * Import one or more CSV files. For each parsed order we detect repeat-customer
 * status then upsert (field-ownership merge). Reports progress via onProgress.
 * Returns a summary { created, updated, skipped, errors[] }.
 */
export async function runImport(files, { onProgress } = {}) {
  const batchId = `import_${Date.now()}`;
  const summary = { created: 0, updated: 0, skipped: 0, errors: [], exceptions: [], total: 0 };
  const deliveryRules = await getDeliveryRules();

  const parsed = [];
  for (const file of files) {
    try {
      const { source, orders, error } = await parseFile(file, deliveryRules);
      if (error) {
        summary.errors.push(error);
        continue;
      }
      parsed.push({ file: file.name, source, orders });
      summary.total += orders.length;
    } catch (e) {
      summary.errors.push(`Failed to read "${file.name}": ${e.message}`);
    }
  }

  let done = 0;
  for (const { source, orders } of parsed) {
    for (const core of orders) {
      try {
        const docId = buildDocId(source, core.platformOrderId);
        const repeat = await detectRepeatCustomer({
          userId: core.buyer?.userId,
          email: core.buyer?.email,
          name: core.buyer?.name,
          address1: core.shipping?.address1,
          excludeDocId: docId,
        });
        const { outcome, exceptions } = await upsertOrder({
          ...core,
          repeatCustomer: repeat,
          meta: { ...(core.meta || {}), importBatchId: batchId },
        });
        if (outcome === 'created') summary.created += 1;
        else summary.updated += 1;
        (exceptions || []).forEach((ex) => {
          const fmt = (v) => (v instanceof Date ? v.toLocaleDateString('en-GB') : String(v ?? ''));
          summary.exceptions.push(
            `Order ${core.platformOrderId}: kept your ${ex.path} ("${fmt(ex.kept)}") — platform now shows "${fmt(ex.incoming)}"`
          );
        });
      } catch (e) {
        summary.errors.push(`Order ${core.platformOrderId}: ${e.message}`);
        summary.skipped += 1;
      }
      done += 1;
      if (onProgress) onProgress({ done, total: summary.total });
    }
  }

  // Refresh KPI aggregates so the dashboard reflects the import (best-effort).
  if (summary.created + summary.updated > 0) {
    try {
      await rebuildStats();
    } catch (e) {
      console.error('Stats rebuild after import failed', e);
    }
  }

  return summary;
}
